import { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { AuditBotPromiseClient } from '@trustero/trustero-api-web/lib/audit/auditbot_grpc_web_pb'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import { RiskServicePromiseClient } from '@trustero/trustero-api-web/lib/risk/risk_grpc_web_pb'
import { GetAuditReadinessRequest } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { GetComplianceFrameworksRequest } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { GetComplianceFrameworkObjectivesRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import { ListVendorsRequest } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { GetRisksRequest } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { ListPoliciesRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useReadAccessChecker } from 'src/app/AppAuth/useReadAccess'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useAccount } from '../../Account/account.hooks'
import {
  DownloadControlsReport,
  ReportExportFormat,
} from '../../../../Utils/reports/ControlsReport'
import { AuditContext } from '../../../../context/AuditContext'
import { useAudit } from '../audit'
import { boolValue, stringValue } from '../../../../Utils/primitives'
import { useFetchChildControlExports } from './useChildControlExports'
import { entityNameFor } from './entityName'

// The rows a grid lists, so the export holds what the user filtered and searched down to.
export type ListedControls = {
  // In display order.
  controlIds: string[]
  // A filter or search narrows the list.
  isFiltered: boolean
}

export type ControlsReportOptions = {
  // Adds the latest control test results as columns. Ignored for a user who may not view them.
  includeTestResults?: boolean
}

// Skipped rather than sent when the user may not read it: the server would deny the call and fail
// the whole export.
const fetchIfReadable = <T,>(
  isReadable: boolean,
  fetch: () => Promise<T>,
): Promise<T | undefined> => (isReadable ? fetch() : Promise.resolve(undefined))

// Everything in the file is fetched when the download is confirmed rather than on mount, so the file
// reflects that moment and nothing in it is missing because a page-load request had not finished.
// The confirmation modal awaits this callback and disables its button while it runs, so the user
// waits on a spinner instead of receiving a half-empty csv.
export const useDownloadControlsReport = (
  auditId?: string,
  format: ReportExportFormat = 'csv',
  listed?: ListedControls,
): ((options?: ControlsReportOptions) => Promise<void>) => {
  const { pageContext } = useParams()
  const { auditId: currentAuditId } = useContext(AuditContext)
  const { data: audit } = useAudit(auditId ?? '', !!auditId)
  const { data: account } = useAccount()
  const fetchChildExports = useFetchChildControlExports(auditId)
  const canRead = useReadAccessChecker()
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)
  const auditBotClient = useAuthorizedGrpcClient(AuditBotPromiseClient)
  const auditClient = useAuthorizedGrpcClient(AuditPromiseClient)
  const vendorClient = useAuthorizedGrpcClient(VendorPromiseClient)
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)

  const invalidAuditId = auditId && !audit
  if (invalidAuditId) {
    return () => Promise.resolve()
  }

  return async ({ includeTestResults = false } = {}) => {
    // An empty control_ids is no filter at all server-side. Unfiltered, every control is what the
    // grid lists, and a reporting parent with none of its own still has its children's rows.
    // Filtered, it would export every control.
    if (listed?.isFiltered && !listed.controlIds.length) return

    const scopedAuditId = auditId ?? currentAuditId
    const withheld = {
      risks: !canRead(CAPABILITY.CAN_VIEW_RISKS),
      vendor: !canRead(CAPABILITY.CAN_VIEW_VENDORS),
      policy: !canRead(CAPABILITY.CAN_VIEW_POLICIES),
      frameworks: !canRead(CAPABILITY.CAN_VIEW_FRAMEWORKS),
    }

    // The requirements are asked for by framework, so they wait on the frameworks.
    const fetchFrameworks = async () => {
      const frameworks = await auditClient.getComplianceFrameworks(
        new GetComplianceFrameworksRequest().setExcludeDisabled(true),
      )
      const requirements = await modelClient.getComplianceFrameworkObjectives(
        new GetComplianceFrameworkObjectivesRequest().setComplianceFrameworkIdsList(
          frameworks.getItemsList().map((cf) => cf.getId()),
        ),
      )
      return { frameworks, requirements }
    }

    const [
      childSnapshots,
      controls,
      policies,
      readiness,
      frameworks,
      departments,
      vendors,
      risks,
      controlStatuses,
    ] = await Promise.all([
      // A child that revoked while this tab sat open must not appear in a file taken after it did.
      // A filtered export leaves children out: the grid never lists them, and its filters hold this
      // account's ids, which cannot select a child's rows.
      listed?.isFiltered ? [] : fetchChildExports(),
      modelClient.listControls(
        new ListControlsRequest()
          .setIsDismissed(boolValue(false))
          .setAuditId(stringValue(scopedAuditId))
          .setUseNewModel(true)
          // the csv is the only reader of procedure/notes/suggestions
          .setIncludeDetails(true)
          .setControlIdsList(listed?.controlIds ?? []),
      ),
      // Audit-scoped like the controls above: /model.Policy/List is auditor-scoped
      // middleware-side, and an unset audit id reads as "no audit" and is denied.
      fetchIfReadable(!withheld.policy, () =>
        policyClient.list(
          new ListPoliciesRequest()
            .setUseNewModel(true)
            .setAuditId(stringValue(scopedAuditId)),
        ),
      ),
      // Scoped like the controls too: without the audit, readiness reports continuous-compliance
      // runs only, and an audit's own results never reach the file.
      fetchIfReadable(
        includeTestResults && canRead(CAPABILITY.CAN_VIEW_AUDIT_SCANS),
        () =>
          auditBotClient.getAuditReadiness(
            new GetAuditReadinessRequest()
              .setAuditId(scopedAuditId ?? '')
              // the export is the only reader of the per-procedure OE details
              .setIncludeTestDetails(true),
          ),
      ),
      fetchIfReadable(!withheld.frameworks, fetchFrameworks),
      // Only a fallback for a control whose department is not set through a linked policy, so the
      // column stays when this cannot be read.
      fetchIfReadable(canRead(CAPABILITY.CAN_VIEW_DEPARTMENTS), () =>
        modelClient.listDepartments(new Empty()),
      ),
      fetchIfReadable(!withheld.vendor, () =>
        vendorClient.listVendors(new ListVendorsRequest()),
      ),
      fetchIfReadable(!withheld.risks, () => {
        const request = new GetRisksRequest()
        if (scopedAuditId) request.setAuditId(scopedAuditId)
        return riskClient.getRisks(request)
      }),
      // Not gated: listing the controls above takes the same capability.
      modelClient.listControlStatuses(new Empty()),
    ])

    if (listed) {
      // ListControls returns model-id order; the file follows the grid's sort instead.
      const position = new Map(
        listed.controlIds.map((controlId, idx) => [controlId, idx]),
      )
      controls.setItemsList(
        [...controls.getItemsList()].sort(
          (a, b) =>
            (position.get(a.getId()) ?? 0) - (position.get(b.getId()) ?? 0),
        ),
      )
    }

    const controlPolicyNamesByControlId = controls
      .getItemsList()
      .reduce((acc: Record<string, string[]>, control) => {
        acc[control.getId()] = [
          ...new Set(
            control
              .getLinkedPoliciesList()
              .map((policy) => policy.getPolicyName())
              .filter(Boolean),
          ),
        ]
        return acc
      }, {})

    // One department entry per linked policy means a control with several policies
    // in the same department would repeat it; dedupe so the export lists it once.
    const controlDepartmentNamesByControlId = controls
      .getItemsList()
      .reduce((acc: Record<string, string[]>, control) => {
        acc[control.getId()] = [
          ...new Set(
            control
              .getLinkedPoliciesList()
              .map((policy) => policy.getDepartment())
              .filter(Boolean),
          ),
        ]
        return acc
      }, {})

    return DownloadControlsReport(
      controls,
      policies,
      controlPolicyNamesByControlId,
      controlDepartmentNamesByControlId,
      departments?.getItemsList(),
      vendors?.getVendorsList(),
      frameworks?.frameworks,
      frameworks?.requirements,
      risks?.getRisksList(),
      controlStatuses.getItemsList(),
      // Undefined, not empty, when not asked for: that is what leaves the test columns out.
      readiness?.getAuditReadinessList(),
      pageContext ?? '',
      new Map(readiness?.getEvidenceLinkIdsMap().toArray() ?? []),
      format,
      {
        name: entityNameFor(account),
        id: account?.getId() ?? '',
        // Empty unless this is a reporting parent. Also empty inside an audit, where a child's
        // continuous-compliance rows do not belong, and in a filtered export.
        childSnapshots,
      },
      withheld,
    )
  }
}
